import { ReactNode } from 'react';
import { Box, Container, Grid, Typography, alpha, Stack, useMediaQuery, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { EditOutlined, TrophyOutlined, HistoryOutlined } from '@ant-design/icons';

// asset
import assessmentResult from 'assets/images/assessment_result.webp';
import assessment from 'assets/images/assessment.webp';
import supportPlan from 'assets/images/support_plan.webp';
import monitoring from 'assets/images/monitoring.webp';
import { AnimatedDigitalRing } from 'sections/AnimatedDigitalRing';
import { SECTION_PY } from 'const/section';

const FeatureSection = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const primaryColor = '#2aa1af';

    // Hero(LandingTop)のリード文にある「個別支援計画書」と同じマーカー風の強調効果
    const Highlight = ({ children }: { children: ReactNode }) => (
        <Box
            component="span"
            sx={{
                position: 'relative',
                fontWeight: 700,
                display: 'inline-block',
                mx: 0.5,
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    bottom: 2,
                    width: '100%',
                    height: '6px',
                    background: alpha(primaryColor, 0.2),
                    borderRadius: '4px',
                    filter: 'blur(2px)',
                    zIndex: -1
                }
            }}
        >
            {children}
        </Box>
    );

    const features: { title: string; desc: ReactNode; icon: JSX.Element; step: string; images: string[] }[] = [
        {
            title: '発達特性を、正しくアセスメント',
            desc: '選択式の画一的なチェックシートではなく、AIとの対話形式でアセスメントが進行。まるで専門家との面談のように、その子ならではの特性を深掘りして引き出します。',
            icon: <EditOutlined />,
            step: '01',
            images: [
                assessment,
                assessmentResult,
                assessmentResult,
            ]
        },
        {
            title: 'AIが目標提案、計画書へ自動反映',
            desc: (
                <>
                    アセスメントに基づき5領域の支援目標を自動設定し、個別支援計画書へ反映。根拠あるアセスメントに基づいて立案された目標だからこそ、<Highlight>保護者面談</Highlight>での説得力や、<Highlight>行政の実地指導</Highlight>への対応力も高まります。
                </>
            ),
            icon: <TrophyOutlined />,
            step: '02',
            images: [
                supportPlan,
            ]
        },
        {
            title: 'モニタリングと成長データの蓄積',
            desc: '過去のデータが蓄積され、成長履歴がしっかり残ります。次回の計画作成時にも過去の変遷をワンクリックで参照・活用できます。',
            icon: <HistoryOutlined />,
            step: '03',
            images: [
                monitoring,
                monitoring,
                monitoring,
            ]
        }
    ];

    const getImageStyles = (imgCount: number, imgIndex: number, step: string) => {
        // STEP 01 の縦長画像3枚向けのスタイル
        if (step === '01' && imgCount === 3) {
            const styles = [
                { top: '5%', left: '25%', scale: 1.2, rotate: -3, width: isMobile ? '45%' : '40%', zIndex: 1 },
                { top: '25%', left: '40%', scale: 1.0, rotate: 2, width: isMobile ? '45%' : '40%', zIndex: 2 },
                { top: '45%', left: '10%', scale: 0.9, rotate: -1, width: isMobile ? '45%' : '40%', zIndex: 3 },
            ];
            return styles[imgIndex];
        }

        // 画像が1つの場合
        if (imgCount === 1) {
            return { top: '15%', left: isMobile ? '10%' : '15%', scale: 1.0, rotate: 0, width: isMobile ? '80%' : '70%', zIndex: 1 };
        }
        // 画像が2つの場合
        if (imgCount === 2) {
            const styles = [
                { top: '10%', left: '5%', scale: 1.0, rotate: -2, width: isMobile ? '70%' : '65%', zIndex: 1 },
                { top: '35%', left: isMobile ? '20%' : '25%', scale: 1.0, rotate: 3, width: isMobile ? '70%' : '65%', zIndex: 2 },
            ];
            return styles[imgIndex];
        }
        // 画像が3つの場合
        const styles = [
            { top: '5%', left: '10%', scale: 1.1, rotate: -3, width: isMobile ? '60%' : '55%', zIndex: 1 },
            { top: '35%', left: '35%', scale: 1.0, rotate: 2, width: isMobile ? '60%' : '55%', zIndex: 2 },
            { top: '65%', left: '5%', scale: 0.9, rotate: -1, width: isMobile ? '60%' : '55%', zIndex: 3 },
        ];
        return styles[imgIndex];
    };

    return (
        <Box component="section" sx={{ bgcolor: '#fff', overflow: 'hidden' }}>
            {/* Container 内のタイトルエリア */}
            <Container sx={{ pt: SECTION_PY, pb: 0 }}>
                <Box sx={{
                    textAlign: 'center',
                    position: 'relative',
                    minHeight: { xs: '200px', md: '300px' },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>

                    {/* 別コンポーネントを呼び出し */}
                    <AnimatedDigitalRing isMobile={isMobile} primaryColor={primaryColor} />

                    {/* テキストコンテンツ */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.5 }}
                        style={{ position: 'relative', zIndex: 1 }}
                    >
                        <Typography
                            variant="h2"
                            // このセクションは機能詳細ページ(/features)の先頭に置かれ、
                            // ページの主見出しになるため h1 にしている
                            component="h1"
                            sx={{
                                fontWeight: 900,
                                fontSize: { xs: '2.2rem', md: '3.6rem' },
                                color: '#1a1a1a',
                                letterSpacing: '-0.04em',
                                mb: 1
                            }}
                        >
                            <Box component="span" sx={{ color: primaryColor }}>OHANA AI</Box> ができること
                        </Typography>
                        <Typography
                            component="p"
                            sx={{
                                fontSize: '0.85rem',
                                fontWeight: 800,
                                color: primaryColor,
                                letterSpacing: '0.3em',
                                textTransform: 'uppercase',
                                opacity: 0.7
                            }}
                        >
                            Core Features
                        </Typography>
                    </motion.div>
                </Box>
            </Container>

            {features.map((feature, index) => {
                const isEven = index % 2 === 0;
                const imgCount = feature.images.length;

                return (
                    <Box
                        key={index}
                        component="article"
                        sx={{
                            // STEP 同士の間隔もセクション間と同じリズムに揃える
                            py: SECTION_PY,
                            position: 'relative',
                            background: isEven
                                ? `linear-gradient(135deg, ${alpha(primaryColor, 0.04)} 0%, #fff 100%)`
                                : 'transparent',
                        }}
                    >
                        <Container>
                            <Grid container spacing={5} alignItems="center" flexDirection={isEven ? 'row' : 'row-reverse'}>
                                <Grid item xs={12} md={5}>
                                    <motion.div
                                        initial={{ opacity: 0, x: isEven ? -40 : 40 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.8, delay: 0.7 }}
                                    >
                                        <Stack spacing={3}>
                                            <Typography
                                                aria-hidden="true"
                                                sx={{ fontSize: '8rem', fontWeight: 900, color: alpha(primaryColor, 0.08), lineHeight: 0.5, mb: -4, fontFamily: 'serif' }}
                                            >
                                                {feature.step}
                                            </Typography>

                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <Box aria-hidden="true" sx={{ p: 1.5, borderRadius: '12px', bgcolor: alpha(primaryColor, 0.1), color: primaryColor, display: 'flex' }}>
                                                    {feature.icon}
                                                </Box>
                                                <Typography component="span" sx={{ fontWeight: 900, color: primaryColor, letterSpacing: '0.1em' }}>STEP {feature.step}</Typography>
                                            </Stack>

                                            <Typography
                                                variant="h3"
                                                component="h3"
                                                sx={{ fontWeight: 800, fontSize: { xs: '1.5rem', md: '2.2rem' }, color: '#1a1a1a' }}
                                            >
                                                {feature.title}
                                            </Typography>

                                            <Typography component="p" sx={{ color: '#555', fontSize: '1.1rem', lineHeight: 1.8 }}>
                                                {feature.desc}
                                            </Typography>
                                            {index == 0 && <Typography
                                                sx={{
                                                    color: alpha('#000', 0.38), // 控えめなグレー
                                                    fontSize: '0.75rem',
                                                    pt: isMobile ? 0.5 : 3,
                                                    lineHeight: 1.6,
                                                    fontWeight: 500
                                                }}
                                            >
                                                ※本アセスメントは脳科学的アプローチによる課題特定を目的としたものではありません。
                                            </Typography>}



                                        </Stack>
                                    </motion.div>
                                </Grid>

                                <Grid item xs={12} md={7}>
                                    <Box sx={{ position: 'relative', height: { xs: '350px', md: '500px' }, width: '100%' }}>
                                        {feature.images.map((img, imgIndex) => {
                                            const style = getImageStyles(imgCount, imgIndex, feature.step);
                                            return (
                                                <motion.div
                                                    key={imgIndex}
                                                    initial={{ opacity: 0, scale: 0.8 }}
                                                    whileInView={{ opacity: 1, scale: style.scale }}
                                                    viewport={{ once: true }}
                                                    transition={{ duration: 0.6, delay: 0.9 + imgIndex * 0.1 }}
                                                    style={{
                                                        position: 'absolute',
                                                        top: style.top,
                                                        left: style.left,
                                                        width: style.width,
                                                        zIndex: style.zIndex,
                                                        rotate: `${style.rotate}deg`,
                                                        opacity: 1,
                                                    }}
                                                >
                                                    <Box
                                                        component="img"
                                                        src={img}
                                                        alt={`${feature.title} の画面イメージ ${imgIndex + 1}`}
                                                        // 画面外のSTEPまで初回に読み込むと LCP が悪化するため遅延読み込みにする
                                                        loading="lazy"
                                                        decoding="async"
                                                        sx={{
                                                            width: '100%',
                                                            height: 'auto',
                                                            borderRadius: '12px',
                                                            bgcolor: '#fff',
                                                            boxShadow: `0 20px 50px ${alpha(primaryColor, 0.25)}, 0 10px 20px ${alpha('#000', 0.1)}`,
                                                            border: '1px solid rgba(255,255,255,0.8)'
                                                        }}
                                                    />
                                                </motion.div>
                                            );
                                        })}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Container>
                    </Box>
                );
            })}
        </Box>
    );
};

export default FeatureSection;